import {
  memo, useCallback, useState, useMemo,
} from 'react';
import {
  Grid,
  Typography,
  TypographyWithTooltip,
  S5,
  S6,
  AG1,
  useTranslations,
  EntityDetailsContainer,
  Label,
  useSnackbar,
} from '@uniqkey-frontend/shared-app';
import ACLEnum from '../../../../../../enums/ACLEnum';
import {
  useGetPartnerUserById,
  useUpdatePartnerUser,
  useUpdatePartnerUserRole,
} from '../../../../../../hooks/reactQuery';
import EditPartnerUserModal, { IEditPartnerUserModalReturnValue } from '../EditPartnerUserModal';
import { useUser } from '../../../../../../contexts/UserContext';
import { logException } from '../../../../../../services/sentryService';
import { USER_ROLES_TRANSLATION_KEYS } from '../../../../../../constants';
import { isAdmin, isLimitedAdmin } from '../../../../../../helpers/userRole';

interface IPartnerUserDetailsWidgetProps {
  partnerUserId: string;
}

const PartnerUserDetailsWidget = (props: IPartnerUserDetailsWidgetProps) => {
  const { partnerUserId } = props;
  const { t } = useTranslations();
  const { showError, showSuccess } = useSnackbar();
  const { currentUser, userCan } = useUser();

  const {
    data: partnerUser,
    isError,
    isLoading: isPartnerUserLoading,
  } = useGetPartnerUserById(
    { partnerUserId },
  );

  const {
    mutateAsync: mutatePartnerUser,
    isLoading: isUpdatePartnerUserLoading,
  } = useUpdatePartnerUser({
    partnerUserId,
    useOptimisticUpdates: true,
  });
  const {
    mutateAsync: mutatePartnerUserRole,
    isLoading: isUpdatePartnerUserRoleLoading,
  } = useUpdatePartnerUserRole({
    partnerUserId,
    useOptimisticUpdates: true,
  });

  const [isEditPartnerUserModalOpen, setIsEditPartnerUserModalOpen] = useState(false);
  const handleEditPartnerUserModalOpen = useCallback(
    () => setIsEditPartnerUserModalOpen(true),
    [],
  );
  const handleEditPartnerUserModalClose = useCallback(
    () => setIsEditPartnerUserModalOpen(false),
    [],
  );

  const { id, role: currentUserRole } = currentUser ?? {};
  const {
    name: partnerUserName,
    email: partnerUserEmail,
    role: partnerUserRole,
  } = partnerUser ?? {};

  const canEditUserRole = useMemo(() => {
    const isCurrentUserLimitedAdmin = isLimitedAdmin(currentUserRole);
    const isTargetUserAdmin = isAdmin(partnerUserRole);
    const canEditRoles = userCan(ACLEnum.PartnerUserEditRoles);
    /*
      Show role selector unless:
        there is no access right for that;
        the current user is Limited Admin, and the selected user is Admin.
    */
    return canEditRoles && !(isCurrentUserLimitedAdmin && isTargetUserAdmin);
  }, [currentUserRole, partnerUserRole, userCan]);

  const handleUpdatePartnerUser = useCallback(async (value: IEditPartnerUserModalReturnValue) => {
    try {
      const { name, email, role } = value;
      await mutatePartnerUser({ partnerUserId, name, email });
      if (canEditUserRole) {
        await mutatePartnerUserRole({ partnerUserId, role });
      }
      showSuccess({ text: t('editPartnerUserModal.partnerUserEdited') });
      handleEditPartnerUserModalClose();
    } catch (e) {
      logException(e, {
        message: 'PartnerUserDetailsWidget/handleUpdatePartnerUser exception',
      });
      showError({ text: t('common.somethingWentWrong') });
    }
  }, [
    mutatePartnerUser,
    mutatePartnerUserRole,
    partnerUserId,
    handleEditPartnerUserModalClose,
    canEditUserRole,
    showError,
    showSuccess,
    t,
  ]);

  if (isError) {
    return null;
  }

  const isCurrentUser = partnerUserId === id;

  const translatedUserRole = t(
    USER_ROLES_TRANSLATION_KEYS[partnerUserRole as keyof typeof USER_ROLES_TRANSLATION_KEYS],
  );

  return (
    <>
      <EntityDetailsContainer
        container
        isLoading={isPartnerUserLoading}
        onClick={handleEditPartnerUserModalOpen}
        editable={userCan(ACLEnum.PartnerUserEdit) && !isCurrentUser}
        justifyContent="space-between"
        rowGap={3}
      >
        <Grid container item flexWrap="nowrap">
          <Grid container item flexDirection="column">
            <Typography variant="caption" color={S5} mb={1}>
              {t('partnerUserDetailsWidget.name')}
            </Typography>
            <TypographyWithTooltip variant="h6" color={S6} breakWord>
              {partnerUserName}
            </TypographyWithTooltip>
          </Grid>
          <Grid item>
            <Label text={translatedUserRole} bgColor={AG1} />
          </Grid>
        </Grid>
        <Grid container item flexDirection="column">
          <Typography variant="caption" color={S5} mb={1}>
            {t('partnerUserDetailsWidget.email')}
          </Typography>
          <TypographyWithTooltip color={S5} breakWord>
            {partnerUserEmail}
          </TypographyWithTooltip>
        </Grid>
      </EntityDetailsContainer>
      {isEditPartnerUserModalOpen && (
        <EditPartnerUserModal
          isOpen={isEditPartnerUserModalOpen}
          isLoading={isUpdatePartnerUserLoading || isUpdatePartnerUserRoleLoading}
          onClose={handleEditPartnerUserModalClose}
          onSubmit={handleUpdatePartnerUser}
          partnerUser={partnerUser!}
        />
      )}
    </>
  );
};

export default memo(PartnerUserDetailsWidget);
